// Tooltip.tsx
import React, {
	useState,
	useRef,
	ReactNode,
	useLayoutEffect,
	CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

export const Tooltip = ({ children, title, content, gap = 8, width = 256 }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [style, setStyle] = useState({});
	const triggerRef = useRef(null);
	const tooltipRef = useRef(null);

	// Recalculate position whenever visibility changes
	useLayoutEffect(() => {
		if (!isVisible || !triggerRef.current || !tooltipRef.current) return;

		const trigRect = triggerRef.current.getBoundingClientRect();
		const tipRect = tooltipRef.current.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		// default: above center
		let top = trigRect.top - tipRect.height - gap;
		let left = trigRect.left + trigRect.width / 2 - tipRect.width / 2;

		// flip to below if not enough space on top
		if (top < 0) {
			top = trigRect.bottom + gap;
		}
		// shift horizontally if overflow
		if (left < 0) left = gap;
		if (left + tipRect.width > vw) left = vw - tipRect.width - gap;

		// shift vertically if overflow bottom
		if (top + tipRect.height > vh) {
			// clamp inside viewport
			top = vh - tipRect.height - gap;
		}

		setStyle({ top: Math.round(top), left: Math.round(left) });
	}, [isVisible, gap, width]);

	return (
		<>
			<div
				ref={triggerRef}
				className="inline-block"
				onMouseEnter={() => setIsVisible(true)}
				onMouseLeave={() => setIsVisible(false)}
				onTouchStart={() => setIsVisible((v) => !v)}
			>
				{children}
			</div>

			{createPortal(
				<AnimatePresence>
					{isVisible && (
						<motion.div
							ref={tooltipRef}
							initial={{ opacity: 0, y: 5 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: 5 }}
							style={{
								position: "fixed",
								width,
								zIndex: 9999,
								pointerEvents: "none",
								...style,
							}}
							className="bg-black/80 text-white text-sm rounded-lg p-3"
						>
							<h4 className="font-medium mb-1">{title}</h4>
							<p className="text-gray-300">{content}</p>
							{/* arrow */}
							<div
								style={{
									position: "absolute",
									width: 8,
									height: 8,
									background: "rgba(0,0,0,0.8)",
									transform: "rotate(45deg)",
									left: "50%",
									marginLeft: -4,
									top:
										style.top &&
										triggerRef.current &&
										style.top < triggerRef.current.getBoundingClientRect().top
											? "100%" // arrow on bottom if tooltip above
											: undefined,
									bottom:
										style.top &&
										triggerRef.current &&
										style.top >=
											triggerRef.current.getBoundingClientRect().bottom
											? "100%" // arrow on top if tooltip below
											: undefined,
								}}
							/>
						</motion.div>
					)}
				</AnimatePresence>,
				document.body
			)}
		</>
	);
};
